import { useEffect, useSyncExternalStore } from 'react';
import { CABECALHO_CSRF } from './auth';
import type { Sessao } from './auth';
import { buscar } from './buscar';

/**
 * FOTO DE PERFIL (0050) — o front: recortar e reduzir no aparelho, enviar, tirar, e saber a foto
 * de cada conta.
 *
 * A tela recorta um QUADRADO do centro e reduz para 256 px (WebP; JPEG onde o navegador não gera
 * WebP) antes de enviar: a foto de 12 MP do celular vira uns 20 KB. O servidor confere tudo de novo
 * — isto é economia de dados, não segurança.
 *
 * A imagem sai de `/api/fotos-de-perfil/:id` (só com login, pelo cookie da sessão). O id muda a
 * cada troca, então serve de versão: foto trocada nunca aparece velha do cache.
 */

/** Lado do quadrado enviado. */
export const LADO_DA_FOTO = 256;
const QUALIDADE = 0.85;

export const enderecoDaFoto = (id: string) => `/api/fotos-de-perfil/${encodeURIComponent(id)}`;

/* ------------------------------------------------------------------ *
 * Recorte no aparelho
 * ------------------------------------------------------------------ */

/** O quadrado central da imagem (a parte que o avatar redondo mostra). */
export function recorteQuadrado(
  largura: number,
  altura: number,
): { readonly x: number; readonly y: number; readonly lado: number } {
  const lado = Math.min(largura, altura);
  return {
    x: Math.round((largura - lado) / 2),
    y: Math.round((altura - lado) / 2),
    lado,
  };
}

function gerar(canvas: HTMLCanvasElement, tipo: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), tipo, QUALIDADE);
  });
}

/** Recorta o centro, reduz para 256×256 e devolve WebP (ou JPEG, se o navegador não fizer WebP). */
export async function prepararFotoDePerfil(arquivo: File): Promise<Blob> {
  let imagem: ImageBitmap;
  try {
    // `from-image`: respeita a rotação que o celular gravou na foto
    imagem = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('Não deu para abrir essa imagem. Escolha uma foto (JPEG, PNG ou WebP).');
  }
  const r = recorteQuadrado(imagem.width, imagem.height);
  const lado = Math.min(LADO_DA_FOTO, r.lado);
  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('Este navegador não consegue preparar a foto.');
  g.imageSmoothingQuality = 'high';
  g.drawImage(imagem, r.x, r.y, r.lado, r.lado, 0, 0, lado, lado);
  imagem.close();
  // Safari antigo devolve PNG quando pede WebP: aí vai JPEG (o servidor só aceita os dois).
  const webp = await gerar(canvas, 'image/webp');
  if (webp && webp.type === 'image/webp') return webp;
  const jpeg = await gerar(canvas, 'image/jpeg');
  if (!jpeg) throw new Error('Falha ao preparar a foto.');
  return jpeg;
}

/* ------------------------------------------------------------------ *
 * Enviar e tirar
 * ------------------------------------------------------------------ */

type Resposta<T> =
  { readonly ok: true; readonly dados: T } | { readonly ok: false; mensagem: string };

async function mensagemDe(r: Response): Promise<string> {
  try {
    return ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    return '';
  }
}

export async function enviarMinhaFoto(
  sessao: Sessao,
  foto: Blob,
): Promise<Resposta<{ foto: string }>> {
  let r: Response;
  try {
    r = await fetch('/api/me/foto', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': foto.type, [CABECALHO_CSRF]: sessao.csrfToken },
      body: foto,
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão para enviar a foto. Tente de novo.' };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as { foto: string } };
  if (r.status === 413) return { ok: false, mensagem: 'Foto grande demais. Escolha outra.' };
  if (r.status === 401) return { ok: false, mensagem: 'Sua sessão acabou. Entre de novo.' };
  return { ok: false, mensagem: (await mensagemDe(r)) || 'A foto não foi aceita.' };
}

export async function tirarMinhaFoto(sessao: Sessao): Promise<Resposta<{ removida: boolean }>> {
  let r: Response;
  try {
    r = await fetch('/api/me/foto', {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { [CABECALHO_CSRF]: sessao.csrfToken },
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão. A foto continua — tente de novo.' };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as { removida: boolean } };
  return { ok: false, mensagem: (await mensagemDe(r)) || 'Não foi possível tirar a foto.' };
}

/* ------------------------------------------------------------------ *
 * A foto de cada conta (Equipe, Premiação…): um pedido serve a tela toda
 * ------------------------------------------------------------------ */

type Mapa = Readonly<Record<string, string>>;
let mapa: Mapa | null = null;
let pedindo = false;
const ouvintes = new Set<() => void>();

function avisar() {
  for (const o of ouvintes) o();
}

async function carregar(): Promise<void> {
  if (pedindo) return;
  pedindo = true;
  try {
    const r = await buscar('/api/fotos-de-perfil', {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    // Falhou: fica sem fotos (as iniciais aparecem) — foto é enfeite, não dado.
    mapa = r.ok ? ((await r.json()) as { fotos: Mapa }).fotos : {};
  } catch {
    mapa = {};
  } finally {
    pedindo = false;
  }
  avisar();
}

/** A foto mudou (troquei a minha): a próxima tela pede a lista de novo. */
export function esquecerFotosDasContas(): void {
  mapa = null;
  if (ouvintes.size > 0) void carregar();
}

function inscrever(o: () => void): () => void {
  ouvintes.add(o);
  return () => {
    ouvintes.delete(o);
  };
}

/** Id da foto da conta, ou null (sem foto, ainda carregando ou conta desconhecida). */
export function useFotoDaConta(accountId: string | null | undefined): string | null {
  const atual = useSyncExternalStore(inscrever, () => mapa);
  useEffect(() => {
    if (mapa === null) void carregar();
  }, []);
  return accountId ? (atual?.[accountId] ?? null) : null;
}
